import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

// ── Types (mirror server response shape) ─────────────────────────────────────
export type Area = {
  id: string;
  name: string;
  type: 'job' | 'business' | 'brand' | 'location' | 'project' | 'personal';
  parentAreaId: string | null;
  color: string | null;
  icon: string | null;
  status: 'active' | 'paused' | 'archived';
  contextMode: 'work' | 'side_hustle' | 'personal';
};

export type Task = {
  id: string;
  areaId: string | null;
  title: string;
  description: string | null;
  status: 'inbox' | 'todo' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: string | null;
  completedAt: string | null;
  source: string;
  createdAt: string;
  /** Set only on dashboard top3 — rules-based auto-priority score */
  score?: number;
  scoreReasons?: string[];
};

export type Note = {
  id: string;
  areaId: string | null;
  title: string | null;
  content: string;
  type: string;
  tags: string[];
  pinned: boolean;
  createdAt: string;
};

export type Workspace = {
  id: string;
  areaId: string | null;
  name: string;
  url: string;
  type: 'website' | 'admin_panel' | 'doc' | 'tool' | 'folder';
  icon: string | null;
  color: string | null;
  position: number;
  credentialId: string | null;
  openCount: number;
  lastUsedAt: string | null;
};

export type Credential = {
  id: string;
  label: string;
  createdAt: string;
  updatedAt: string;
};

export type DecryptedCredential = {
  username: string;
  password: string;
  notes?: string;
};

export type ReminderLink = { label: string; url: string };

export type Reminder = {
  id: string;
  workspaceId: string | null;
  areaId: string | null;
  title: string;
  description: string | null;
  severity: 'info' | 'warning' | 'critical';
  surfaceMode: 'dashboard_banner' | 'workspace_card' | 'pre_open_modal' | 'all';
  links: ReminderLink[];
};

export function useAcknowledgeReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; snoozeUntil?: string }) =>
      api.post(`/reminders/${input.id}/acknowledge`, {
        snoozeUntil: input.snoozeUntil,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reminders'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export type InboxItem = {
  id: string;
  rawContent: string;
  source: string;
  status: 'pending' | 'processing' | 'promoted' | 'discarded';
  createdAt: string;
};

export type DashboardData = {
  top3Tasks: Task[];
  activeTasks: Task[];
  areas: Area[];
  workspaces: Workspace[];
  inbox: InboxItem[];
  reminders: Reminder[];
  recentActivity: Array<{
    id: string;
    action: string;
    entityType: string | null;
    createdAt: string;
  }>;
};

export type Me = {
  id: string;
  email: string;
  name: string;
  locale: string;
  timezone: string;
};

// ── Auth ─────────────────────────────────────────────────────────────────────
export function useMe() {
  return useQuery<Me | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const res = await api.get<{ data: Me }>('/auth/me');
        return res.data;
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<{ data: { user: Me; expiresAt: string } }>('/auth/login', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      qc.clear();
      window.location.assign('/login');
    },
  });
}

// ── Dashboard ────────────────────────────────────────────────────────────────
export function useDashboard() {
  return useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: async () => (await api.get<{ data: DashboardData }>('/dashboard/daily')).data,
  });
}

// ── Areas ────────────────────────────────────────────────────────────────────
export function useAreas() {
  return useQuery<Area[]>({
    queryKey: ['areas'],
    queryFn: async () => (await api.get<{ data: Area[] }>('/areas')).data,
  });
}

export function useCreateArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Area> & { name: string; type: Area['type'] }) =>
      api.post<{ data: Area }>('/areas', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['areas'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/areas/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['areas'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ── Tasks ────────────────────────────────────────────────────────────────────
export function useTasks(filters?: { areaId?: string; status?: string }) {
  const qs = new URLSearchParams();
  if (filters?.areaId) qs.set('areaId', filters.areaId);
  if (filters?.status) qs.set('status', filters.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<Task[]>({
    queryKey: ['tasks', filters],
    queryFn: async () => (await api.get<{ data: Task[] }>(`/tasks${suffix}`)).data,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      areaId?: string | null;
      priority?: Task['priority'];
      status?: Task['status'];
      dueDate?: string | null;
      description?: string | null;
    }) => api.post<{ data: Task }>('/tasks', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: Task }>(`/tasks/${id}/complete`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/tasks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ── Notes ────────────────────────────────────────────────────────────────────
export function useNotes() {
  return useQuery<Note[]>({
    queryKey: ['notes'],
    queryFn: async () => (await api.get<{ data: Note[] }>('/notes')).data,
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { content: string; title?: string | null; areaId?: string | null }) =>
      api.post<{ data: Note }>('/notes', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes'] }),
  });
}

// ── Workspaces ───────────────────────────────────────────────────────────────
export function useWorkspaces() {
  return useQuery<Workspace[]>({
    queryKey: ['workspaces'],
    queryFn: async () => (await api.get<{ data: Workspace[] }>('/workspaces')).data,
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      url: string;
      type?: Workspace['type'];
      areaId?: string | null;
      icon?: string | null;
      color?: string | null;
      position?: number;
    }) => api.post<{ data: Workspace }>('/workspaces', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaces'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useArchiveWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/workspaces/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaces'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useTrackWorkspaceOpen() {
  return useMutation({
    mutationFn: (id: string) => api.post(`/workspaces/${id}/track-open`),
  });
}

// ── Vault ────────────────────────────────────────────────────────────────────
export function useCredentials() {
  return useQuery<Credential[]>({
    queryKey: ['vault'],
    queryFn: async () => (await api.get<{ data: Credential[] }>('/vault')).data,
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      label: string;
      workspaceId?: string | null;
      payload: { username: string; password: string; notes?: string };
    }) => api.post<{ data: Credential }>('/vault', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vault'] });
      qc.invalidateQueries({ queryKey: ['workspaces'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      label?: string;
      payload?: { username: string; password: string; notes?: string };
    }) =>
      api.patch(`/vault/${input.id}`, {
        ...(input.label !== undefined ? { label: input.label } : {}),
        ...(input.payload !== undefined ? { payload: input.payload } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vault'] }),
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/vault/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vault'] });
      qc.invalidateQueries({ queryKey: ['workspaces'] });
    },
  });
}

/** Decrypts on demand. Returns plaintext — handle carefully (don't cache, copy then forget). */
export async function fetchDecryptedCredential(id: string): Promise<DecryptedCredential> {
  const res = await api.get<{ data: DecryptedCredential }>(`/vault/${id}/decrypt`);
  return res.data;
}

export async function fetchBookmarklet(
  id: string,
): Promise<{ bookmarkletUrl: string; instructions: string }> {
  const res = await api.get<{
    data: { bookmarkletUrl: string; instructions: string };
  }>(`/vault/${id}/bookmarklet`);
  return res.data;
}

// ── Briefing ─────────────────────────────────────────────────────────────────
export type Briefing = {
  id: string;
  date: string;
  content: string;
  modelUsed: string;
  costUsd: string | null;
  deliveredTelegramAt: string | null;
  deliveredDashboardAt: string | null;
  createdAt: string;
};

export function useBriefing() {
  return useQuery<Briefing | null>({
    queryKey: ['briefing'],
    queryFn: async () => (await api.get<{ data: Briefing | null }>('/dashboard/briefing')).data,
    staleTime: 5 * 60_000,
  });
}

export function useRegenerateBriefing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ data: Briefing }>('/dashboard/briefing/regenerate'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['briefing'] });
    },
  });
}

export function useDeleteBriefing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/dashboard/briefing/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['briefing'] });
    },
  });
}

export function useDeleteAllBriefings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<{ data: { count: number } }>('/dashboard/briefings'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['briefing'] });
    },
  });
}

export function useDeleteAllInbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del<{ data: { count: number } }>('/inbox'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useRegenerateWeeklyReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ data: { id: string; content: string } }>('/dashboard/weekly-review/regenerate'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['briefing'] }),
  });
}

// ── Settings ─────────────────────────────────────────────────────────────────
export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post('/settings/change-password', input),
  });
}

export type ApiKey = {
  id: string;
  name: string;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
};

export function useApiKeys() {
  return useQuery<ApiKey[]>({
    queryKey: ['api-keys'],
    queryFn: async () => (await api.get<{ data: ApiKey[] }>('/settings/api-keys')).data,
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; scopes?: string[]; expiresInDays?: number }) =>
      api.post<{
        data: { id: string; name: string; plaintext: string; message: string };
      }>('/settings/api-keys', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/settings/api-keys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['api-keys'] }),
  });
}

// ── Shopping ─────────────────────────────────────────────────────────────────
export type ShoppingTag = 'amazon' | 'private' | 'thaihoang' | 'other';
export type ShoppingStatus = 'pending' | 'bought' | 'cancelled';

export type ShoppingItem = {
  id: string;
  item: string;
  qty: number;
  tag: ShoppingTag;
  notes: string | null;
  url: string | null;
  priceEur: string | null;
  status: ShoppingStatus;
  boughtAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export function useShopping(filters?: { tag?: ShoppingTag; status?: ShoppingStatus }) {
  const qs = new URLSearchParams();
  if (filters?.tag) qs.set('tag', filters.tag);
  if (filters?.status) qs.set('status', filters.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery<ShoppingItem[]>({
    queryKey: ['shopping', filters],
    queryFn: async () => (await api.get<{ data: ShoppingItem[] }>(`/shopping${suffix}`)).data,
  });
}

export function useCreateShopping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      item: string;
      qty?: number;
      tag?: ShoppingTag;
      notes?: string | null;
      url?: string | null;
      priceEur?: number | null;
    }) => api.post<{ data: ShoppingItem }>('/shopping', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shopping'] }),
  });
}

export function useUpdateShopping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: Partial<ShoppingItem> }) =>
      api.patch(`/shopping/${input.id}`, input.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shopping'] }),
  });
}

export function useToggleShoppingBought() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; bought: boolean }) =>
      api.post(`/shopping/${input.id}/${input.bought ? 'check' : 'uncheck'}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shopping'] }),
  });
}

export function useDeleteShopping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/shopping/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shopping'] }),
  });
}

// ── Google integration ───────────────────────────────────────────────────────
export type GoogleStatus = {
  configured: boolean;
  connected: boolean;
  email: string | null;
  scopes: string[];
  connectedAt: string | null;
};

export function useGoogleStatus() {
  return useQuery<GoogleStatus>({
    queryKey: ['google-status'],
    queryFn: async () => (await api.get<{ data: GoogleStatus }>('/auth/google/status')).data,
    refetchOnWindowFocus: true,
  });
}

export function useDisconnectGoogle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/google/disconnect'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['google-status'] }),
  });
}

// ── Inbox ────────────────────────────────────────────────────────────────────
export function useCapture() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { content: string; source?: 'web' | 'voice' | 'email' }) =>
      api.post<{ data: InboxItem }>('/inbox/capture', { source: 'web', ...input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useInbox() {
  return useQuery<InboxItem[]>({
    queryKey: ['inbox'],
    queryFn: async () => (await api.get<{ data: InboxItem[] }>('/inbox')).data,
  });
}

export function usePromoteInbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      type: 'task' | 'note';
      title?: string;
      areaId?: string | null;
    }) =>
      api.post(`/inbox/${input.id}/promote`, {
        type: input.type,
        title: input.title,
        areaId: input.areaId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['notes'] });
    },
  });
}

export function useDiscardInbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/inbox/${id}/discard`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
